import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import { get, post } from "../../util/http";
import { isShareView } from "../../share/shareView";
import "./LoadTestToggle.scss";

/*
 * Development-only control. This drives loadTestRunner, which opens real
 * sockets and writes real sessions/events for a site - it must never be
 * reachable by a paying customer looking at their own live dashboard.
 *
 * The API is already refused in production (LoadTestController returns 403
 * when NODE_ENV === 'production'), so this gate is the SECOND layer, not the
 * only one. It exists because a visible switch that silently does nothing is
 * its own problem: it shipped in the production bundle and read as "test
 * traffic is running against my real site" to the person looking at it.
 *
 * `process.env.NODE_ENV` is inlined by the build - 'development' under
 * `npm start`, 'production' for every real build - so this whole component is
 * dead code eliminated from shipped bundles rather than merely hidden.
 */
const LoadTestToggle = () => {
  const authReducer: any = useSelector((state: any) => state?.AuthReducer);
  const siteId = authReducer?.authData?.site?._id;

  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(false);

  // never in a recipient's replay clip: under `npm start` it sat on top of the clock
  const devOnly = process.env.NODE_ENV === "development" && !isShareView();

  useEffect(() => {
    if (!devOnly) return;
    if (!siteId) return;
    get("utility/loadTest/status").then((result: any) => {
      if (result?.data?.running) setRunning(true);
    });
  }, [siteId, devOnly]);

  const handleToggle = async () => {
    if (!siteId || loading) return;
    setLoading(true);
    try {
      if (running) {
        await post("utility/loadTest/stop", {});
        setRunning(false);
      } else {
        await post("utility/loadTest/start", { siteId });
        setRunning(true);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!devOnly) return null;
  if (!siteId) return null;

  return (
    <div className="load-test-toggle">
      <label className="load-test-switch">
        <input
          type="checkbox"
          checked={running}
          disabled={loading}
          onChange={handleToggle}
        />
        <span className="load-test-slider" />
      </label>
      <span className="load-test-toggle-label">
        Test traffic {running ? "on" : "off"}
      </span>
    </div>
  );
};

export default LoadTestToggle;
